import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './lib/firebase';
import { useStore } from './store';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import NewGame from './pages/NewGame';
import GameDetails from './pages/GameDetails';
import SharedView from './pages/SharedView';
import Analytics from './pages/Analytics';
import Profile from './pages/Profile';
import Feedback from './pages/Feedback';
import FeedbackDetails from './pages/FeedbackDetails';
import Layout from './components/Layout';
import Upvotes from './pages/Upvotes';
import CoachDashboard from './pages/CoachDashboard';
import CoachProfile from './pages/CoachProfile';
import StudentManagement from './pages/StudentManagement';
import GoalieCoaches from './pages/GoalieCoaches';
import AITrainer from './pages/AITrainer';
import AITrainerSession from './pages/AITrainerSession';
import AdminPanel from './pages/AdminPanel';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const session = useStore((state) => state.user);
  const location = useLocation();

  if (!session) {
    // Save the attempted URL
    return <Navigate to='/login' state={{ from: location }} />;
  }

  return children;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const session = useStore((state) => state.user);
  const location = useLocation();

  // Check if there's a redirect path in the location state
  const from = location.state?.from?.pathname || '/dashboard';

  if (session) {
    return <Navigate to={from} />;
  }

  return children;
}

function App() {
  const setUser = useStore((state) => state.setUser);
  const subscribeToLimits = useStore((state) => state.subscribeToLimits);
  const unsubscribeFromLimits = useStore(
    (state) => state.unsubscribeFromLimits
  );
  const syncProfileCounts = useStore((state) => state.syncProfileCounts);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);

      // Sync profile counts when user logs in
      if (user) {
        syncProfileCounts(user.uid);
      }
    });

    return () => unsubscribe();
  }, [setUser, syncProfileCounts]);

  // Subscribe to limits changes on app startup
  useEffect(() => {
    const limitsUnsubscribe = subscribeToLimits();

    return () => {
      if (limitsUnsubscribe) {
        limitsUnsubscribe();
      }
      unsubscribeFromLimits();
    };
  }, [subscribeToLimits, unsubscribeFromLimits]);

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path='/' element={<Navigate to='/login\' replace />} />
        <Route
          path='/login'
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path='/register'
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
        <Route
          path='/dashboard'
          element={
            <PrivateRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path='/new-game'
          element={
            <PrivateRoute>
              <Layout>
                <NewGame />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path='/game/:id'
          element={
            <PrivateRoute>
              <Layout>
                <GameDetails />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path='/shared/:shareId'
          element={
            <PrivateRoute>
              <Layout>
                <SharedView />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path='/analytics'
          element={
            <PrivateRoute>
              <Layout>
                <Analytics />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path='/ai-trainer'
          element={
            <PrivateRoute>
              <Layout>
                <AITrainer />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path='/ai-trainer/:sessionId'
          element={
            <PrivateRoute>
              <Layout>
                <AITrainerSession />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path='/feedback'
          element={
            <PrivateRoute>
              <Layout>
                <Feedback />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path='/feedback/:id'
          element={
            <PrivateRoute>
              <Layout>
                <FeedbackDetails />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path='/profile'
          element={
            <PrivateRoute>
              <Layout>
                <Profile />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path='/upvotes'
          element={
            <PrivateRoute>
              <Layout>
                <Upvotes />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path='/coach-dashboard'
          element={
            <PrivateRoute>
              <Layout>
                <CoachDashboard />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path='/coach-profile'
          element={
            <PrivateRoute>
              <Layout>
                <CoachProfile />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path='/student-management'
          element={
            <PrivateRoute>
              <Layout>
                <StudentManagement />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path='/goalie-coaches'
          element={
            <Layout>
              <GoalieCoaches />
            </Layout>
          }
        />
        <Route path='/admin' element={<AdminPanel />} />
        {/* Catch-all redirect */}
        <Route path='*' element={<Navigate to='/login\' replace />} />
      </Routes>
    </Router>
  );
}

export default App;